window.BAO.Brands = {
  identifier: 'data-brands-el',
  currentTab: 'brands',
  get els () {
    const keys = new Set(
      getElements(`[${this.identifier}]`).map(
        element => element.dataset.brandsEl,
      ),
    )
    const array = [...keys].map(key => {
      return {
        [key]: {
          get selector () {
            return `[${window.BAO.Brands.identifier}="${key}"]`
          },
          get element () {
            return this.elements?.[0]
          },
          get elements () {
            return getElements(this.selector)
          },
          get exists () {
            return !!this.element
          },
        },
      }
    })
    return array.reduce((a, b) => Object.assign(a, b), {})
  },
  get sectionId () {
    return window.BAO.Brands.els.container.element
      .closest('.shopify-section')
      .getAttribute('id')
      .replace('shopify-section-', '')
  },
  showFavouriteBrands (context) {
    getElements(window.BAO.Brands.els?.['brand-item']?.selector, {
      context,
    })?.forEach(brandEl => {
      const activeFavouriteEl = getElement(
        '[is="favourites-heart"].wlt-Heart-active',
        { context: brandEl },
      )
      brandEl.setAttribute('aria-hidden', String(!activeFavouriteEl))
    })
  },
  hideEmptySections (context) {
    getElements(window.BAO.Brands.els?.section?.selector, {
      context,
    })?.forEach(sectionEl => {
      const visibleBrandEls = getElements(
        `${window.BAO.Brands.els?.['brand-item']?.selector}[aria-hidden="false"]`,
        { context: sectionEl },
      )
      sectionEl.setAttribute('aria-hidden', String(!visibleBrandEls?.length))

      if (sectionEl.hasAttribute('id')) {
        getElement(`[href='#${sectionEl.getAttribute('id')}']`)?.setAttribute(
          'data-item-count',
          String(visibleBrandEls.length),
        )
      }

      sectionEl.removeAttribute('data-last')
    })
  },
  togglePromoElements (isVisible) {
    Brands.els?.promo?.elements.forEach(promoEl => {
      promoEl.setAttribute('aria-hidden', String(!isVisible))
    })
  },
  visibleSectionEls (context) {
    return getElements(
      `${window.BAO.Brands.els?.section?.selector}[aria-hidden='false']`,
      { context },
    )
  },
  removePromoElements (context) {
    getElements(Brands.els.promo?.selector, {
      context,
    }).forEach(promoEl => promoEl.remove())
  },
  renderDrawerFavourites () {
    if (
      Brands.els?.['body-desktop']?.exists &&
      Brands.els?.['body-mobile']?.exists
    ) {
      Brands.els['body-mobile'].element.innerHTML =
        Brands.els?.['body-desktop'].element.innerHTML

      Brands.filterFavourites(Brands.els['body-mobile'].element)
      Brands.removePromoElements(Brands.els['body-mobile'].element)
    }
  },
  filterFavourites (context) {
    Brands.showFavouriteBrands(context)
    Brands.hideEmptySections(context)

    Brands.toggleNoFavouritesMessaging(
      !Brands.visibleSectionEls(context).length,
    )
  },
  toggleNoFavouritesMessaging (isVisible) {
    getElements(Brands.els['no-favourites']?.selector, {
      context: Brands.els['body-desktop'].element,
    })?.forEach(messageEl => {
      if (Brands.currentTab === 'favourites') {
        messageEl.setAttribute('aria-hidden', String(!isVisible))
      } else {
        messageEl.setAttribute('aria-hidden', 'true')
      }
    })

    getElements(Brands.els['no-favourites']?.selector, {
      context: Brands.els['body-mobile'].element,
    })?.forEach(messageEl => {
      messageEl.setAttribute('aria-hidden', String(!isVisible))
    })
  },
  showAll (context) {
    const brandEls = getElements(Brands.els?.['brand-item']?.selector, {
      context,
    })
    brandEls?.forEach(brandEl => {
      brandEl.setAttribute('aria-hidden', 'false')
    })

    const sectionEls = getElements(Brands.els?.section?.selector, {
      context,
    })
    sectionEls?.forEach(brandSectionEl => {
      brandSectionEl.setAttribute('aria-hidden', 'false')
    })
  },
  async renderFavourites (mobileOnly) {
    const desktopBodyEl = Brands.els?.['body-desktop']?.element
    const mobileBodyEl = Brands.els?.['body-mobile']?.element

    if (desktopBodyEl && mobileBodyEl) {
      Brands.els.container.element.setAttribute('aria-busy', true)

      const html = await fetchSingle(Brands.sectionId, window.location.pathname)

      if (html?.length) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
        const sectionEl = getElement(Brands.els?.['body-desktop']?.selector, {
          context: parsedHTML,
        })

        Brands.filterFavourites(sectionEl)

        if (!mobileOnly) {
          desktopBodyEl.innerHTML = sectionEl.innerHTML
        }
        mobileBodyEl.innerHTML = sectionEl.innerHTML

        Brands.removePromoElements(mobileBodyEl)

        Brands.els.container.element.setAttribute('aria-busy', false)
      }
    }
  },
  reset: () => {
    Brands.toggleNoFavouritesMessaging(false)
    window.BAO.Brands?.els?.section?.elements?.forEach(sectionEl => {
      sectionEl.setAttribute('aria-hidden', 'false')
    })
    window.BAO.Brands?.els?.['brand-item']?.elements?.forEach(brandEl => {
      brandEl.setAttribute('aria-hidden', 'false')
    })
  },
  resetTabs () {
    document.dispatchEvent(new CustomEvent('reset-tabs'))
  },
  resetCategories () {
    document.dispatchEvent(new CustomEvent('reset-categories'))
  },
  resetSearch () {
    document.dispatchEvent(new CustomEvent('reset-search'))
  },
}

const { Brands } = window.BAO
const { getElement, getElements } = window.BAO.Utils
const { fetchSingle } = window.BAO.sectionRenderer

export class BrandsNavigation extends window.BAO.CustomElement({
  SuperClass: HTMLElement,
}) {
  static PRIORITY_CARD_LISTS_COUNT = 3

  static get requiredElements () {
    return ['link']
  }

  constructor () {
    super()
    this.isScrollingToSection = false
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.hash?.length) {
      this.disableNavigationHighlight = true
      this.applyPaginationOnPageLoad(this.hash)
    }

    // Mark priority product-card-list elements in a-to-j section
    this.markPriorityElements('a-to-j')

    // Preload all pagination sections so Klevu requests batch together
    this.preloadPaginationSections()
  }

  markPriorityElements (key) {
    const targetContainer = this.els.paginationContent?.elements.find(
      el => el.dataset.key === key,
    )

    if (targetContainer) {
      const productCardLists = targetContainer.querySelectorAll(
        'product-card-list',
      )
      productCardLists.forEach((el, index) => {
        if (index < BrandsNavigation.PRIORITY_CARD_LISTS_COUNT) {
          el.dataset.priority = 'true'
        }
      })
    }
  }

  async preloadPaginationSections () {
    await Promise.all([
      this.renderPaginationSection('k-to-r'),
      this.renderPaginationSection('s-to-z'),
      this.renderPaginationSection('0-to-9'),
    ])

    this.setupIntersectionObserver()
  }

  setupListeners () {
    super.setupListeners()

    this.handlePaginationClick = this.handlePaginationClick.bind(this)
    this.handleLink = this.handleLink.bind(this)

    this.els.pagination?.elements?.forEach(paginationEl => {
      this.listeners.add(paginationEl, 'click', this.handlePaginationClick)
    })

    this.els.paginationNext?.elements?.forEach(paginationEl => {
      paginationEl.addEventListener('click', this.handlePaginationClick)
    })

    if (this.els?.link?.exists) {
      this.listeners.add(this, 'click', this.handleClick.bind(this))
    }

    document.querySelectorAll('.brd-Brand_Link').forEach(el => {
      this.listeners.add(el, 'click', this.handleLink)
    })

    document.querySelectorAll('.brd-Brand_Image').forEach(el => {
      this.listeners.add(el.querySelector('a'), 'click', this.handleLink)
    })

    this.updateFades = this.updateFades.bind(this)

    this.els.list.element.addEventListener('scroll', this.updateFades)
    window.addEventListener('resize', this.updateFades)

    this.updateFades()

    this.els.arrowRight.element.addEventListener('click', () => {
      this.els.list.element.scrollBy({
        left: 200,
        behavior: 'smooth',
      })
    })

    this.els.arrowLeft.element.addEventListener('click', () => {
      this.els.list.element.scrollBy({
        left: -200,
        behavior: 'smooth',
      })
    })
  }

  async handlePaginationClick (event) {
    const key = event.target.dataset.key

    const letter = this.querySelector(
      `${this.els?.link?.selector}[data-key="${key}"]`,
    )

    letter?.click()
  }

  async renderPaginationSection (key, loading = false) {
    // Only render for k-r, s-z, and 0-9 sections (a-j is server-side rendered)
    if (key === 'a-to-j') return

    const targetContainer = this.els.paginationContent.elements.find(
      el => el.dataset.key === key,
    )

    if (
      !targetContainer ||
      targetContainer.hasAttribute('data-section-loaded')
    ) {
      return
    }

    const sectionMap = {
      'k-to-r': 'template-brands-k-r',
      's-to-z': 'template-brands-s-z',
      '0-to-9': 'template-brands-0-9',
    }

    const sectionId = sectionMap[key]
    if (!sectionId) return

    Brands.els.container.element.setAttribute('aria-busy', loading)

    try {
      const html = await fetchSingle(sectionId, window.location.pathname)

      if (html?.length) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
        const sectionEl = getElement('.brd-Brands_PaginationContainer', {
          context: parsedHTML,
        })

        if (sectionEl) {
          targetContainer.innerHTML = sectionEl.innerHTML
          targetContainer.setAttribute('data-section-loaded', 'true')

          // Mark first N product-card-list elements as priority for immediate rendering
          const productCardLists = targetContainer.querySelectorAll(
            'product-card-list',
          )
          productCardLists.forEach((el, index) => {
            if (index < BrandsNavigation.PRIORITY_CARD_LISTS_COUNT) {
              el.dataset.priority = 'true'
            }
          })

          if (!targetContainer.querySelector('.brd-Letter_Brand')) {
            this.querySelectorAll(
              `[data-brands-navigation-el="paginationNext"][data-key="${key}"]`,
            ).forEach(el => {
              el.remove()
            })
          }
        }
      }
    } catch (error) {
      console.error(`Failed to load section ${sectionId}:`, error)
    } finally {
      Brands.els.container.element.setAttribute('aria-busy', false)
    }
  }

  handleLink (el, e) {
    const letter = el.target
      .closest('.brd-Letter')
      .querySelector('[data-brands-el="letter"]')
      .dataset.letter.toLowerCase()

    const currentUrl = window.location.href

    // Push the new history state with the updated hash

    history.replaceState(null, '', `${currentUrl.split('#')[0]}${letter}`)
  }

  async applyPaginationOnPageLoad (hash) {
    const linkEl = this.els.link.elements.find(
      el => el.dataset.brandUrl === hash,
    )

    if (linkEl) {
      const key = linkEl.dataset.key

      // Section-render the appropriate brands section if needed
      await this.renderPaginationSection(key, true)

      this.handlePaginationUIUpdate(key)

      this.setCurrentLink(this.hash)
    }
  }

  handlePaginationUIUpdate (key) {
    this.els.paginationContent.elements.forEach(el =>
      el.setAttribute('aria-hidden', el.dataset.key !== key),
    )

    this.els.paginationNext.elements.forEach(el => {
      el.setAttribute('aria-hidden', el.dataset.viewKey !== key)
    })
  }

  setupIntersectionObserver () {
    const options = {
      root: null,
      rootMargin: '0px 0px -100%',
      scrollMargin: '53px',
      threshold: 0,
    }

    const buttons = [
      ...document.querySelectorAll('[data-brands-navigation-el="link"]'),
    ]

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (this.isScrollingToSection) return

        if (!entry.isIntersecting) return

        const targetId = entry.target.dataset.letter.toLowerCase()
        const button = buttons.find(
          button => button.dataset.brandUrl === targetId,
        )

        buttons.forEach(button => {
          button?.setAttribute('aria-current', 'false')
        })
        button?.setAttribute('aria-current', 'true')

        const list = this.els.list?.element
        if (list) {
          list.scrollTo({
            left:
              button.parentElement.offsetLeft -
              list.clientWidth / 2 +
              button.clientWidth / 2,
            behavior: 'smooth',
          })
        }
      })
    }, options)

    document
      .querySelectorAll('[data-brands-el="section"]')
      .forEach(el => observer.observe(el))
  }

  handleClick (event) {
    const clickedLinkEl = event.target.closest(this.els?.link?.selector)
    const currentLinkEl = document.querySelector(
      '.brd-Navigation_Link[aria-current="true"]',
    )
    const letter = clickedLinkEl?.dataset.brandLetter.toLowerCase()

    if (!clickedLinkEl) return

    const section = document.querySelector(`#brand-${letter}`)
    const currentSection = document.querySelector(
      currentLinkEl.dataset.brandUrl,
    )

    const isBefore = currentSection.offsetTop > section.offsetTop
    const isChangingPage =
      this.els.paginationContent.elements
        .find(el => el.dataset.key === clickedLinkEl.dataset.key)
        .getAttribute('aria-hidden') === 'true'

    this.handlePaginationUIUpdate(clickedLinkEl.dataset.key)

    if (!section) {
      console.warn(`Section #brand-${letter} not found in DOM`)
      // Still update the current link state even if section doesn't exist
      clickedLinkEl?.setAttribute('aria-current', 'true')
      if (letter) {
        history.pushState(null, '', `#brand-${letter}`)
      }
      return
    }

    currentLinkEl
      ?.closest('.brd-Navigation_Items')
      ?.setAttribute('data-lock-intersection', 'true')

    if (currentLinkEl) {
      currentLinkEl.removeAttribute('aria-current')

      // Get the top position of the section
      const sectionTop =
        section.getBoundingClientRect().top + window.pageYOffset

      // Determine the scroll offset based on the scroll direction
      const headerHeight = document.querySelector('.hd-Header').offsetHeight
      const navHeight = this.els.list.element.offsetHeight

      let offset =
        isBefore || sectionTop < 500 ? headerHeight + navHeight : navHeight

      if (isChangingPage) {
        offset = document.body.classList.contains('lyt-Header-hidden')
          ? navHeight
          : headerHeight + navHeight
      }

      // Scroll to the section with the appropriate offset
      this.isScrollingToSection = true
      window.scrollTo({
        top: sectionTop - offset,
        behavior: isChangingPage ? 'instant' : 'smooth',
      })
      setTimeout(() => {
        this.isScrollingToSection = false
      }, 1000)
    }

    clickedLinkEl?.setAttribute('aria-current', 'true')

    if (letter) {
      history.pushState(null, '', `#brand-${letter}`)
    }

    const list = this.els.list?.element
    if (list) {
      list.scrollTo({
        left:
          clickedLinkEl.parentElement.offsetLeft -
          list.clientWidth / 2 +
          clickedLinkEl.clientWidth / 2,
        behavior: 'smooth',
      })
    }
  }

  setCurrentLink (href) {
    this.els.link.elements.forEach(linkEl => {
      linkEl.setAttribute('aria-current', href === linkEl.dataset.brandUrl)
    })
  }

  clickCurrentLinkEl () {
    this.els.link.elements
      .find(linkEl => linkEl.getAttribute('aria-current') === 'true')
      ?.click()
  }

  updateFades () {
    if (!this.els.list) return

    const container = this.els.list?.element?.parentElement

    const atStart = this.els.list.element.scrollLeft <= 1
    const atEnd =
      this.els.list.element.scrollLeft + this.els.list.element.clientWidth >=
      this.els.list.element.scrollWidth - 1

    if (container) {
      container.classList.toggle('no-left', atStart)
      container.classList.toggle('no-right', atEnd)
    }
  }

  get hash () {
    return window.location.hash
  }

  get rect () {
    return this.getBoundingClientRect()
  }
}

if (!customElements.get('brands-navigation')) {
  customElements.define('brands-navigation', BrandsNavigation, {
    extends: 'section',
  })
}
